import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Router } from '@angular/router';
import { MetaService } from '@macs-frontend/web-macs/core/services/meta.service';
import {
  APIResponse,
  Company,
  CompanyPublic,
  Currency,
  LoadUser,
  Role,
  RoleUnit,
  TeamMember,
  Timezone,
  User,
  UserStatuses,
} from '@macs-frontend/web-macs/shared';
import { Actions, createEffect } from '@ngrx/effects';
import { Action, select, Store } from '@ngrx/store';
import { ofAction } from 'macs-ngrx-actions';
import { forkJoin, Observable, of } from 'rxjs';
import {
  catchError,
  map,
  mergeMap,
  switchMap,
  tap,
  withLatestFrom,
} from 'rxjs/operators';
import * as metaActions from '../features/shared/actions/meta.actions';
import * as fromRoot from '../reducers/';
import * as fromMeta from '../reducers/meta';

@Injectable()
export class MetaEffects {
  showNotification$ = createEffect(
    () =>
      this.actions$.pipe(
        ofAction(metaActions.ShowNotification),
        tap(() => {
          this.router.navigate([{ outlets: { modal: 'notifications' } }], {
            queryParamsHandling: 'merge',
            preserveFragment: true,
          });
        }),
      ),
    { dispatch: false },
  );

  loadConsts$: Observable<Action> = createEffect(() =>
    this.actions$.pipe(
      ofAction(metaActions.LoadConsts, metaActions.LoadFinancialConsts),
      switchMap(action =>
        forkJoin([
          this.metaApiService.loadCurrencies(),
          this.metaApiService.loadLanguages(),
          this.metaApiService.loadTimezones(),
        ]).pipe(
          map(
            ([currencies, languages, timezones]: [
              APIResponse<Currency[]>,
              APIResponse<string[]>,
              APIResponse<Timezone[]>,
            ]) =>
              new metaActions.ConstsLoaded({
                currencies,
                languages,
                timezones,
              }),
          ),
          catchError(error => of(new metaActions.LoadFail(error))),
        ),
      ),
    ),
  );

  loadUser$: Observable<Action> = createEffect(() =>
    this.actions$.pipe(
      ofAction(metaActions.LoadUser),
      switchMap(action =>
        this.metaApiService.loadCurrentUser().pipe(
          tap((response: APIResponse<User>) => {
            const user = response.data;
            if (user.clientUser && user.status === UserStatuses.PENDING) {
              this.router.navigate(['/personal-profile']);
            }
          }),
          map((user: APIResponse<User>) => new metaActions.UserLoaded(user)),
          catchError(error => of(new metaActions.LoadFail(error))),
        ),
      ),
    ),
  );

  loadRoleUnits$: Observable<Action> = createEffect(() =>
    this.actions$.pipe(
      ofAction(metaActions.LoadRoleUnits),
      switchMap(action =>
        this.metaApiService.loadRoleUnits().pipe(
          map(
            (roleUnits: APIResponse<Role[]>) =>
              new metaActions.RoleUnitsLoaded(roleUnits),
          ),
          catchError(error => of(new metaActions.LoadFail(error))),
        ),
      ),
    ),
  );

  saveRoleUnit$: Observable<Action> = createEffect(() =>
    this.actions$.pipe(
      ofAction(metaActions.SaveRoleUnit),
      withLatestFrom(this.store$.pipe(select(fromMeta.getRoleUnits))),
      mergeMap(([action, roleUnits]) => {
        // @ts-expect-error
        const currentRoleUnit: RoleUnit = roleUnits.find(
          u => u.uuid === action.payload.roleUnitId,
        );
        // @ts-expect-error
        const currentTeamMember: TeamMember = action.payload.project.teamMembers.find(
          m => m.user.uuid === action.payload.userId,
        );
        const roleUnit = {
          ...currentRoleUnit,
          units: [
            ...currentRoleUnit.units.map(u => ({
              ...(u.user && { user: u.user.uuid }),
              ...(u.project && { project: u.project.uuid }),
              ...(u.company && { company: u.company.uuid }),
              ...(u.role && { role: u.role.uuid }),
            })),
            {
              project: action.payload.project.uuid,
              user: currentTeamMember.user.uuid,
            },
          ],
        };
        return this.metaApiService.saveRoleUnit(action.payload.roleUnitId, roleUnit).pipe(
          map((u: APIResponse<Role>) => new metaActions.RoleUnitUpdated(u.data)),
          catchError(error => of(new metaActions.LoadFail(error))),
        );
      }),
    ),
  );

  loadCurrentCompany$: Observable<Action> = createEffect(() =>
    this.actions$.pipe(
      ofAction(metaActions.LoadCurrentCompany),
      switchMap(action => {
        return this.metaApiService.loadCurrentCompany().pipe(
          map(
            (resp: APIResponse<Company>) =>
              new metaActions.CurrentCompanyLoaded(resp.data),
          ),
          catchError(error => of(new metaActions.LoadFail(error))),
        );
      }),
    ),
  );

  changeCurrentCompany$: Observable<Action> = createEffect(() =>
    this.actions$.pipe(
      ofAction(metaActions.ChangeCurrentCompany),
      switchMap(action =>
        this.metaApiService.changeCurrentCompany(action.payload).pipe(
          map((resp: { token: string }) => {
            localStorage.setItem('token', resp.token);
            return new metaActions.LoadCurrentCompany();
          }),
          catchError(error => of(new metaActions.LoadFail(error))),
        ),
      ),
    ),
  );

  loadCurrentPublicCompany$: Observable<Action> = createEffect(() =>
    this.actions$.pipe(
      ofAction(metaActions.LoadCurrentPublicCompany),
      switchMap(action =>
        this.metaApiService.loadCurrentPublicCompany().pipe(
          map(
            (resp: APIResponse<CompanyPublic>) =>
              new metaActions.CurrentCompanyPublicLoaded(resp.data),
          ),
          catchError(error => of(new metaActions.LoadFail(error))),
        ),
      ),
    ),
  );

  constructor(
    private readonly actions$: Actions,
    private readonly http: HttpClient,
    private readonly store$: Store<fromRoot.State>,
    private readonly metaApiService: MetaService,
    private readonly router: Router,
  ) {}
}
